import {Link as RoutedLink} from 'react-router-dom';

import {Button} from 'shells/button/button';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Link} from 'shells/link/link';
import {Body} from 'shells/typography/body';

import type {ThreadType} from 'nutshell-graphql-types';

import {redirectToFacebookOAuth} from '../../facebook/helpers';

export const getBrandDisplayName = (threadType: ThreadType) => {
    switch (threadType) {
        case 'MESSENGER':
            return 'Messenger';
        case 'INSTAGRAM':
            return 'Instagram';
        case 'WHATSAPP':
            return 'WhatsApp Business';
        default:
            return '';
    }
};

export const getAuthProviderName = (threadType: ThreadType) => {
    switch (threadType) {
        case 'MESSENGER':
        case 'INSTAGRAM':
            return 'Facebook';
        case 'WHATSAPP':
            return 'WhatsApp Business';
        default:
            return '';
    }
};

export const getLearnMoreLink = (threadType: ThreadType) => {
    switch (threadType) {
        case 'MESSENGER':
            return 'https://www.messenger.com/';
        case 'INSTAGRAM':
            return 'https://www.instagram.com/';
        case 'WHATSAPP':
            return 'https://business.whatsapp.com/';
        default:
            return '';
    }
};

export const getManageConnectionLink = (threadType: ThreadType) => {
    switch (threadType) {
        case 'MESSENGER':
        case 'INSTAGRAM':
            return '/inbox/settings/facebook';
        case 'WHATSAPP':
            return '/inbox/settings/whatsapp';
        default:
            return '';
    }
};

export const getStatusButtonText = (
    isConnected: boolean,
    isEnabled: boolean,
    threadType: ThreadType,
    phoneNumber?: string | null
) => {
    const authProviderName = getAuthProviderName(threadType);

    if (!isConnected) {
        return `Connect to ${authProviderName}`;
    } else if (isConnected && isEnabled) {
        if (threadType === 'WHATSAPP' && phoneNumber) {
            return `Configured: ${phoneNumber}`;
        }

        return `${authProviderName} is connected`;
    } else if (isConnected && !isEnabled) {
        return `Enable your ${authProviderName} integration`;
    }

    return '';
};

export const getHeaderContent = (
    isConnected: boolean,
    isEnabled: boolean,
    threadType: ThreadType
) => {
    let content = '';
    const authProviderName = getAuthProviderName(threadType);

    if (isConnected && isEnabled) {
        content = `Your ${authProviderName} account is connected`;
    } else if (isConnected && !isEnabled) {
        content = `Enable your ${authProviderName} integration`;
    } else if (!isConnected) {
        content = `Connect to ${authProviderName}`;
    }

    return <Body>{content}</Body>;
};

// For whatsapp, we need to redirect to the settings page so we can load the
// Facebook SDK used for login. For Facebook login, we can just launch the Oauth
// flow directly from the popover.
export const getLoginButton = (threadType: ThreadType, isEmbedded: boolean) => {
    switch (threadType) {
        case 'MESSENGER':
        case 'INSTAGRAM':
            return (
                <Button
                    onClick={() => redirectToFacebookOAuth(isEmbedded)}
                    variant='primary'
                    size='big'
                >
                    Continue to Facebook
                </Button>
            );
        case 'WHATSAPP':
            return (
                <Link
                    // @ts-expect-error
                    as={RoutedLink}
                    to='/inbox/settings/whatsapp'
                    variant='button-primary'
                    size='big'
                >
                    Continue to WhatsApp settings
                </Link>
            );
        default:
            return null;
    }
};

export const getPopoverContent = (
    isConnected: boolean,
    isEnabled: boolean,
    isEmbedded: boolean,
    threadType: ThreadType
) => {
    let content;

    const authProviderName = getAuthProviderName(threadType);
    const brandDisplayName = getBrandDisplayName(threadType);
    const manageConnectionLink = getManageConnectionLink(threadType);

    if (!isConnected) {
        content = (
            <StackLegacy spacing={16} alignCenter={true}>
                <Body>
                    To connect with people who message you on {brandDisplayName}, you’ll need to log
                    in to your {authProviderName} account.
                </Body>
                {getLoginButton(threadType, isEmbedded)}
                <Link
                    href={getLearnMoreLink(threadType)}
                    variant='primary'
                    size='small'
                    newTab={true}
                >
                    Learn more about {brandDisplayName}
                </Link>
            </StackLegacy>
        );
    } else if (isEnabled) {
        content = (
            <StackLegacy spacing={16} alignCenter={true}>
                <Body>
                    {authProviderName} is properly configured. Incoming messages will show up in
                    your inbox.
                </Body>
                {/* @ts-expect-error */}
                <Link as={RoutedLink} to={manageConnectionLink} variant='primary'>
                    Manage connection
                </Link>
            </StackLegacy>
        );
    } else {
        content = (
            <StackLegacy spacing={16} alignCenter={true}>
                <Body>
                    Your {authProviderName} account is connected, but the integration is not
                    enabled. Enable the integration to start receiving messages.
                </Body>
                {/* @ts-expect-error */}
                <Link as={RoutedLink} to={manageConnectionLink} variant='primary'>
                    Manage connection
                </Link>
            </StackLegacy>
        );
    }

    return <div className='pad-24 flex justify-center'>{content}</div>;
};
